import { useEval } from '@/context/EvalContext';
import { useState, useEffect } from 'react';
const GRADE_OPTIONS = ['大一', '大二', '大三', '大四', '研一', '研二', '研三'];
const SUBJECT_OPTIONS = ['生涯教育', '思想政治', '职业规划', '心理健康', '通识教育', '创新创业'];

const inputBase: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(34,211,238,0.22)',
  borderRadius: '7px',
  color: 'white',
  fontSize: '16px',
  padding: '7px 10px',
  outline: 'none',
  width: '100%',
  transition: 'border-color 0.2s',
};

export default function BasicInfoPanel() {
  const evalData = useEval();
  // We use state to allow temporary edits, but seed it from evalData once it's ready.
  // Delay render until evalData is available.
  const [info, setInfo] = useState({
    schoolName: '',
    teacherName: '',
    courseName: '',
    subject: '',
    grade: '',
    major: '',
  });

  const [focused, setFocused] = useState<string | null>(null);

  useEffect(() => {
    if (evalData?.courseEvalData?.basicInfo) {
      setInfo({ ...evalData.courseEvalData.basicInfo });
    } else {
      setInfo({
        schoolName: '',
        teacherName: '',
        courseName: '',
        subject: '',
        grade: '',
        major: '',
      });
    }
  }, [evalData]);

  const getInputStyle = (key: string): React.CSSProperties => ({
    ...inputBase,
    borderColor: focused === key ? 'rgba(34,211,238,0.6)' : 'rgba(34,211,238,0.22)',
    boxShadow: focused === key ? '0 0 10px rgba(34,211,238,0.18)' : 'none',
  });

  return (
    <div className="rounded-xl overflow-hidden relative" style={{
      background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(10,20,40,0.88) 100%)',
      border: '1px solid rgba(34,211,238,0.22)',
      backdropFilter: 'blur(16px)',
    }}>
      {/* 标题栏 */}
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: '1px solid rgba(34,211,238,0.12)' }}>
        <div className="w-1 h-4 rounded-full flex-shrink-0"
          style={{ background: 'linear-gradient(to bottom, #22d3ee, rgba(34,211,238,0.5))', boxShadow: '0 0 8px rgba(34,211,238,0.8)' }} />
        <span className="text-white font-bold font-semibold tracking-wide" style={{ fontSize: '22px' }}>基本信息</span>
      </div>

      {/* 表单内容 */}
      <div className="px-4 py-3 flex flex-col gap-3">
        {/* 学校名称 + 教师姓名 */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-white font-bold block mb-1.5" style={{ fontSize: '16px' }}>学校名称</label>
            <input
              style={getInputStyle('school')}
              placeholder="请输入学校名称"
              value={info.schoolName}
              onFocus={() => setFocused('school')}
              onBlur={() => setFocused(null)}
              onChange={e => setInfo(v => ({ ...v, schoolName: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-white font-bold block mb-1.5" style={{ fontSize: '16px' }}>教师姓名</label>
            <input
              style={getInputStyle('teacher')}
              placeholder="请输入教师姓名"
              value={info.teacherName}
              onFocus={() => setFocused('teacher')}
              onBlur={() => setFocused(null)}
              onChange={e => setInfo(v => ({ ...v, teacherName: e.target.value }))}
            />
          </div>
        </div>

        {/* 课程名称 */}
        <div>
          <label className="text-white font-bold block mb-1.5" style={{ fontSize: '16px' }}>课程名称</label>
          <input
            style={getInputStyle('course')}
            value={info.courseName}
            onFocus={() => setFocused('course')}
            onBlur={() => setFocused(null)}
            onChange={e => setInfo(v => ({ ...v, courseName: e.target.value }))}
          />
        </div>

        {/* 学科 + 学生年级 */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-white font-bold block mb-1.5" style={{ fontSize: '16px' }}>学科</label>
            <select
              style={{ ...getInputStyle('subject'), cursor: 'pointer', appearance: 'auto', color: info.subject ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.3)' }}
              value={info.subject || ''}
              onFocus={() => setFocused('subject')}
              onBlur={() => setFocused(null)}
              onChange={e => setInfo(v => ({ ...v, subject: e.target.value }))}
            >
              <option value="" disabled hidden style={{ background: '#0a1428', color: 'white' }}>未选择</option>
              {SUBJECT_OPTIONS.map(s => (
                <option key={s} value={s} style={{ background: '#0a1428', color: '#e2e8f0' }}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-white font-bold block mb-1.5" style={{ fontSize: '16px' }}>学生年级</label>
            <select
              style={{ ...getInputStyle('grade'), cursor: 'pointer', appearance: 'auto', color: info.grade ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.3)' }}
              value={info.grade || ''}
              onFocus={() => setFocused('grade')}
              onBlur={() => setFocused(null)}
              onChange={e => setInfo(v => ({ ...v, grade: e.target.value }))}
            >
              <option value="" disabled hidden style={{ background: '#0a1428', color: 'white' }}>未选择</option>
              {GRADE_OPTIONS.map(g => (
                <option key={g} value={g} style={{ background: '#0a1428', color: '#e2e8f0' }}>{g}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 学生专业 */}
        <div>
          <label className="text-white font-bold block mb-1.5" style={{ fontSize: '16px' }}>学生专业</label>
          <input
            style={getInputStyle('major')}
            placeholder="请输入大类/方向名称"
            value={info.major}
            onFocus={() => setFocused('major')}
            onBlur={() => setFocused(null)}
            onChange={e => setInfo(v => ({ ...v, major: e.target.value }))}
          />
        </div>
      </div>
    </div>
  );
}
